import type { DataSourceStatus } from "@adpilot/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  StatusBar,
  StatusBarPlot,
  type StatusBarStatus,
  StatusBarTooltip,
} from "@/components/charts/status-bar";

export const FRESHNESS_COLORS = {
  synced: "#059669",
  recent: "#2563eb",
  pending: "#d97706",
  attention: "#e11d48",
  unknown: "var(--muted-foreground)",
} as const;

type Day = NonNullable<DataSourceStatus["reportQuality"]>["days"][number];

interface DataFreshnessBarProps {
  days: Day[];
  shopName: string;
  sourceName: string;
  secondaryDays?: Day[];
  secondaryName?: string;
}

export function DataFreshnessBar({
  days,
  shopName,
  sourceName,
  secondaryDays,
  secondaryName,
}: DataFreshnessBarProps) {
  const { t, i18n } = useTranslation("settings");
  const formatters = useMemo(
    () => ({
      short: new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
    }),
    [i18n.language],
  );
  const formatDate = (date: string) => formatters.short.format(new Date(`${date}T00:00:00Z`));

  if (!days.length) {
    return <p className="text-xs text-muted-foreground">{t("dataStatus.daysUnavailable")}</p>;
  }

  const statuses: StatusBarStatus[] = [
    { id: "synced", label: t("dataStatus.dayStates.synced"), color: FRESHNESS_COLORS.synced },
    { id: "recent", label: t("dataStatus.dayStates.recent"), color: FRESHNESS_COLORS.recent },
    { id: "pending", label: t("dataStatus.dayStates.pending"), color: FRESHNESS_COLORS.pending },
    {
      id: "attention",
      label: t("dataStatus.dayStates.attention"),
      color: FRESHNESS_COLORS.attention,
    },
    {
      id: "unknown",
      label: t("dataStatus.dayStates.unknown"),
      color: FRESHNESS_COLORS.unknown,
    },
  ];
  const secondaryByDate = new Map(secondaryDays?.map((day) => [day.date, day]));
  const dayStatus = (day: Day | undefined) =>
    !day ? "unknown" : day.status === "synced" && day.recent ? "recent" : day.status;
  const secondaryColors: Record<string, string> = {};
  const data = days.map((day) => {
    const secondary = dayStatus(secondaryByDate.get(day.date));
    secondaryColors[day.date] = FRESHNESS_COLORS[secondary];
    return {
      id: day.date,
      label: formatDate(day.date),
      status: dayStatus(day),
      description: secondaryName
        ? `${secondaryName}: ${t(`dataStatus.dayStates.${secondary}`)}`
        : undefined,
    };
  });
  return (
    <StatusBar
      data={data}
      className="max-w-[740px]"
      statuses={statuses}
      label={t("dataStatus.dayGroup", { shop: shopName, source: sourceName })}
    >
      <StatusBarPlot secondaryColors={secondaryDays ? secondaryColors : undefined} />
      <StatusBarTooltip />
    </StatusBar>
  );
}
